import React, { useState } from 'react';
import { Drawer, Table } from 'antd';
import type { TableColumnsType, TablePaginationConfig } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { formatTimestampToDateTime } from 'utils/dateTimeUtils';
import {
    LIST_SCAN_SETTING_LOGS,
    ListScanSettingLogsResponse,
    PdaScanSettingLog,
} from '../graphql/pdaScanConfigQueries';

interface Props {
    open: boolean;
    onClose: () => void;
    accountId?: string;
    warehouseId?: number;
    title?: string;
}

const ScanSettingLogDrawer: React.FC<Props> = ({ open, onClose, accountId, warehouseId, title }) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const { data, loading } = useQuery<ListScanSettingLogsResponse>(LIST_SCAN_SETTING_LOGS, {
        variables: {
            accountId: accountId ? Number(accountId) : undefined,
            warehouseId,
            page,
            pageSize,
        },
        skip: !open || (!accountId && !warehouseId),
        fetchPolicy: 'network-only',
    });

    const columns: TableColumnsType<PdaScanSettingLog> = [
        { title: t('pdaScanConfig.log.action'), dataIndex: 'action', key: 'action' },
        { title: t('pdaScanConfig.log.operatorType'), dataIndex: 'operatorType', key: 'operatorType' },
        { title: t('pdaScanConfig.log.operatorName'), dataIndex: 'operatorName', key: 'operatorName', render: (v: string | null) => v ?? '-' },
        { title: t('pdaScanConfig.log.changedFields'), dataIndex: 'changedFieldsJson', key: 'changedFieldsJson', render: (v: string | null) => v ?? '-' },
        { title: t('pdaScanConfig.log.source'), dataIndex: 'source', key: 'source' },
        {
            title: t('pdaScanConfig.log.createdAt'),
            dataIndex: 'createdAtMs',
            key: 'createdAtMs',
            render: (v: number | null) => (v ? formatTimestampToDateTime(v) : '-'),
        },
    ];

    const pagination: TablePaginationConfig = {
        current: page,
        pageSize,
        total: data?.listScanSettingLogs.total ?? 0,
        showSizeChanger: true,
        onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
        },
    };

    return (
        <Drawer title={title ?? t('pdaScanConfig.log.title')} open={open} onClose={onClose} width={900}>
            <Table<PdaScanSettingLog>
                rowKey={(r, i) => `${r.accountId}-${r.createdAtMs}-${i}`}
                loading={loading}
                columns={columns}
                dataSource={data?.listScanSettingLogs.content ?? []}
                pagination={pagination}
            />
        </Drawer>
    );
};

export default ScanSettingLogDrawer;
